import { breakpoint } from '@porsche-design-system/components-js/styles';
import { Component, Element, Event, EventEmitter, Fragment, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { RoutingKeyPointers, RoutingKeyWithClick } from '@phn/entities/routing-key';
import { routerStore } from '@phn/state/router-store';
import { NavigationMode, NavigationModeType, NavigationTheme, NavigationThemeType } from '@phn/utility/constants';
import { constructThemeClass } from '../../utility/helper';
import { isDesktop, IsKeyboardClick } from '@phn/utility/helper';
import { isShopAvailable } from '@phn/utility/helper/shop-helper';
import { componentsOnReady } from '@phn/utility/ready';
import { Timeout } from '@phn/utility/timeout';
import * as routing from '@phn/services/routing-manager';
import { navStateStore } from '@phn/state/nav-state-store';
import featureToggles from '@phn/services/feature-toggles';
import NavigationDrawerLoader from './navigation-drawer-loader';
import OldNavigationDrawer from './old-navigation-drawer';
import { Application, ApplicationType } from '@phn/utility/constants/app';
import { closeNavigationDrawer, openNavigationDrawer } from '@phn/services/navigation-drawer-service';

@Component({
  tag: 'phn-level-0',
  styleUrl: 'level-0.scss',
  scoped: true
})
export class Level0 {
  @Prop() locale!: string;
  @Prop() breakpoint!: number;
  @Prop() loadingContent = false;
  @Prop() receivedContent = false;
  @Prop() theme: NavigationThemeType = NavigationTheme.light;
  @Prop() mode: NavigationModeType = NavigationMode.navbar;
  @Prop() app: ApplicationType = Application.default;
  @Prop() displayLogoOnly = false;
  @Prop() limitedWidth = false;

  @State() usingKeyboard = false;
  @State() scrolledPastHeader = false;
  @State() isNavigationLoaded = false;
  @State() isLoading = false;
  @State() isMyPorscheHoveredOn = false;

  @Element() el!: HTMLPhnLevel0Element;

  @Event() navigationLoaded!: EventEmitter<boolean>;

  private logoLoaded = false;
  private drawerTimeout = new Timeout(20000);

  @Watch('loadingContent')
  loadingContentWatcher(): void {
    this.setNavigationLoaded();
  }

  @Watch('breakpoint')
  breakpointWatcher(): void {
    this.updateRoutingKeyPointerOfMobileLevel1();
    this.setNavigationLoaded();
  }

  @Watch('receivedContent')
  receivedContentWatcher(): void {
    this.setNavigationLoaded();
  }

  @Listen('logoLoaded')
  logoLoadedHandler(): void {
    this.logoLoaded = true;
    this.setNavigationLoaded();
  }

  @Listen('scroll', { target: 'window', passive: true })
  scrollHandler(): void {
    this.scrolledPastHeader = this.el.getBoundingClientRect().bottom <= 0;
  }

  @Listen('keydown', { target: 'window', passive: true })
  handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.usingKeyboard = true;
      navStateStore.state.usingKeyboard = true;

      if (featureToggles.isNaviDrawerFromCDNEnabled()) {
        closeNavigationDrawer();
        return;
      }
      if (!routing.isClosed()) {
        routing.reset();
        return;
      }
    }
  }

  @Listen('click', { passive: true })
  handleClick(e: MouseEvent): void {
    this.usingKeyboard = IsKeyboardClick(e);
    navStateStore.state.usingKeyboard = IsKeyboardClick(e);
  }

  @Listen('openDrawer')
  async handleOpenDrawer(event: CustomEvent<RoutingKeyWithClick>): Promise<void> {
    await this.openDrawer(event.detail);
  }

  private updateRoutingKeyPointerOfMobileLevel1() {
    if (routerStore.state.routingKeyPointer === RoutingKeyPointers.LEVEL_ONE) {
      routerStore.state.routingKeyPointer = isDesktop(this.breakpoint)
        ? RoutingKeyPointers.LEVEL_TWO
        : RoutingKeyPointers.LEVEL_ONE;
    }
  }

  private setNavigationLoaded(): void {
    this.isNavigationLoaded = !this.loadingContent && this.logoLoaded;
    this.navigationLoaded.emit(this.isNavigationLoaded);
  }

  private async openDrawer(routingKeyWithClick: RoutingKeyWithClick): Promise<void> {
    const { initialRoutingKey, isKeyboardClick } = routingKeyWithClick;

    this.isLoading = true;
    this.usingKeyboard = isKeyboardClick;
    navStateStore.state.usingKeyboard = isKeyboardClick;

    const backdropComponent = featureToggles.isNaviDrawerFromCDNEnabled() ? 'phn-nd-backdrop' : 'phn-backdrop';

    this.openMenuDrawer(initialRoutingKey || navStateStore.state.initialRoutingPath || '');

    await componentsOnReady(this.el, this.drawerTimeout, '.drawer', backdropComponent);
    this.isLoading = false;
  }

  private openMenuDrawer(initialRoutingKeys: string) {
    if (featureToggles.isNaviDrawerFromCDNEnabled()) {
      openNavigationDrawer(initialRoutingKeys);
      return;
    }

    routing.goTo(...routing.mountRouteFromString(initialRoutingKeys));
  }

  disconnectedCallback(): void {
    this.drawerTimeout.cancel();
  }

  private getLogoSize(): string {
    return this.breakpoint >= breakpoint.m ? 'medium' : 'small';
  }

  render(): HTMLPhnLevel0Element {
    return (
      <Host class={`${constructThemeClass(this.mode, this.theme)}-theme themed-background`}>
        <header class={this.limitedWidth ? 'limited-width' : ''}>
          {routing.isOpened() || routing.isOpening() ? (
            <phn-backdrop style={this.isLoading ? { display: 'none' } : {}} />
          ) : null}

          <div class="level-0-content">
            {!this.displayLogoOnly ? (
              <div class="level-0-content-icons">
                <phn-level-0-icons
                  locale={this.locale}
                  theme={this.theme}
                  mode={this.mode}
                  app={this.app}
                  breakpoint={this.breakpoint}
                  isLoading={this.isLoading}
                  isNavigationLoaded={this.isNavigationLoaded}
                >
                  <div class="level-0-content-logo">
                    <phn-logo
                      app={this.app}
                      locale={this.locale}
                      theme={this.theme}
                      mode={this.mode}
                      size={this.getLogoSize()}
                      breakpoint={this.breakpoint}
                      style={this.isNavigationLoaded ? {} : { display: 'none' }}
                    />
                    {!this.isNavigationLoaded ? (
                      <div class="crest-skeleton skeleton-container">
                        {this.breakpoint > breakpoint.xs ? (
                          this.mode === NavigationMode.hero && this.theme === NavigationTheme.dark ? (
                            <span class="label-bone-dark" />
                          ) : (
                            <span class="label-bone" />
                          )
                        ) : this.mode === NavigationMode.hero && this.theme === NavigationTheme.dark ? (
                          <span class="crest-bone-dark" />
                        ) : (
                          <span class="crest-bone" />
                        )}
                      </div>
                    ) : null}
                  </div>
                </phn-level-0-icons>
                {isShopAvailable(this.locale, this.app) ? (
                  <Fragment>
                    {featureToggles.isNaviDrawerFromCDNEnabled() ? null : (
                      <contextual-drawer>
                        <div slot="contextual_drawer_header">
                          <slot name="contextual_drawer_header"></slot>
                        </div>
                      </contextual-drawer>
                    )}
                  </Fragment>
                ) : null}
              </div>
            ) : (
              <div class="level-0-content-logo">
                <phn-logo
                  app={this.app}
                  locale={this.locale}
                  theme={this.theme}
                  mode={this.mode}
                  size={this.getLogoSize()}
                  breakpoint={this.breakpoint}
                  style={this.isNavigationLoaded ? {} : { display: 'none' }}
                />
                {!this.isNavigationLoaded ? (
                  <div class="crest-skeleton skeleton-container">
                    {this.breakpoint > breakpoint.xs ? (
                      this.mode === NavigationMode.hero && this.theme === NavigationTheme.dark ? (
                        <span class="label-bone-dark" />
                      ) : (
                        <span class="label-bone" />
                      )
                    ) : this.mode === NavigationMode.hero && this.theme === NavigationTheme.dark ? (
                      <span class="crest-bone-dark" />
                    ) : (
                      <span class="crest-bone" />
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </header>

        {featureToggles.isNaviDrawerFromCDNEnabled() ? (
          <NavigationDrawerLoader />
        ) : (
          <OldNavigationDrawer
            locale={this.locale}
            breakpoint={this.breakpoint}
            usingKeyboard={this.usingKeyboard}
            isLoading={this.isLoading}
            receivedContent={this.receivedContent}
          />
        )}
      </Host>
    ) as HTMLPhnLevel0Element;
  }
}
